/**
 * @voltxampere/ui — RubberBandTool (Sprint 13)
 *
 * Click + drag on empty space to lasso a selection rectangle.
 * Modifier keys mirror Sprint 12's SelectTool semantics:
 *
 *   no modifier → replace selection
 *   shift       → add to selection
 *   cmd / ctrl  → toggle (xor with current selection)
 *
 * The selection rectangle is exposed as a `selection-box`
 * SceneNode (Sprint 12 already declared the kind); the renderer
 * paints it on the `selection` layer.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export class RubberBandTool {
    id = "rubber-band";
    cursor = "crosshair";
    state = { type: "idle" };
    previewNodeId = "vxa-rubber-band";
    onExit;
    constructor(opts = {}) {
        if (opts.onExit !== undefined)
            this.onExit = opts.onExit;
    }
    onPointerDown(e, ctx) {
        if (e.button !== 0)
            return;
        const world = ctx.worldFromEvent(e);
        // If the press lands on a hit-target, the canvas should have
        // routed to a different tool — fall through with no-op.
        if (ctx.hitTester.hit(world) !== null)
            return;
        const modifier = e.shiftKey
            ? "add"
            : e.metaKey || e.ctrlKey
                ? "toggle"
                : "replace";
        this.state = { type: "banding", start: world, current: world, modifier };
        try {
            ctx.canvas.setPointerCapture(e.pointerId);
        }
        catch {
            // ignore
        }
        ctx.invalidate();
    }
    onPointerMove(e, ctx) {
        if (this.state.type !== "banding")
            return;
        this.state = { ...this.state, current: ctx.worldFromEvent(e) };
        ctx.invalidate();
    }
    onPointerUp(e, ctx) {
        if (this.state.type !== "banding")
            return;
        const rect = bandRect(this.state.start, this.state.current);
        const hits = ctx.hitTester.hitRect(rect);
        const ids = hits.map((n) => n.id);
        const current = ctx.selectedNodes.value;
        let next;
        switch (this.state.modifier) {
            case "replace":
                next = ids;
                break;
            case "add":
                next = [...new Set([...current, ...ids])];
                break;
            case "toggle": {
                const set = new Set(current);
                for (const id of ids) {
                    if (set.has(id))
                        set.delete(id);
                    else
                        set.add(id);
                }
                next = [...set];
                break;
            }
        }
        ctx.selectedNodes.value = next;
        this.state = { type: "idle" };
        try {
            if (ctx.canvas.hasPointerCapture(e.pointerId)) {
                ctx.canvas.releasePointerCapture(e.pointerId);
            }
        }
        catch {
            // ignore
        }
        ctx.invalidate();
        if (this.onExit)
            this.onExit();
    }
    onKeyDown(e, ctx) {
        if (e.key === "Escape" && this.state.type === "banding") {
            this.state = { type: "idle" };
            ctx.invalidate();
            if (this.onExit)
                this.onExit();
        }
    }
    onLeave(ctx) {
        if (this.state.type === "banding") {
            this.state = { type: "idle" };
            ctx.invalidate();
        }
    }
    /** Renderer surface — emit the selection-box SceneNode. */
    previewNodes() {
        if (this.state.type !== "banding")
            return [];
        return [
            {
                kind: "selection-box",
                id: this.previewNodeId,
                from: this.state.start,
                to: this.state.current,
            },
        ];
    }
    /** Test surface. */
    get isBanding() {
        return this.state.type === "banding";
    }
}
function bandRect(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}
//# sourceMappingURL=rubber-band.js.map